import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Mail, Loader2, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react'
import { ApiResponse } from '../../services/api'

interface ForgotPasswordProps {
  onResetPassword: (email: string) => Promise<ApiResponse<any>>
  onBackToLogin?: () => void
  isLoading?: boolean
}

interface ForgotPasswordFormData {
  email: string
}

interface ErrorState {
  type: 'success' | 'error'
  message: string
}

export default function ForgotPassword({ onResetPassword, onBackToLogin, isLoading = false }: ForgotPasswordProps) {
  const [errorState, setErrorState] = useState<ErrorState | null>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  
  const { register, handleSubmit, formState: { errors } } = useForm<ForgotPasswordFormData>()


  const handleResetPassword = async (data: ForgotPasswordFormData) => {
    setErrorState(null)
    
    try {
      const response = await onResetPassword(data.email)
      
      if (response && !response.error) {
        setErrorState({
          type: 'success',
          message: 'Password reset instructions have been sent to your email address. Please check your inbox and follow the instructions to reset your password.'
        })
        setIsSubmitted(true)
      } else if (response && response.error) {
        setErrorState({
          type: 'error',
          message: getErrorMessage(response.error)
        })
      } else {
        setErrorState({
          type: 'error',
          message: 'Failed to send reset instructions. Please try again.'
        })
      }
    } catch {
      setErrorState({
        type: 'error',
        message: 'An unexpected error occurred. Please try again.'
      })
    }
  }

  const getErrorMessage = (error: any): string => {
    switch (error.code) {
      case 'CONFIG_ERROR':
        return 'The application is not properly configured. Please contact your system administrator.'
      case 'NETWORK_ERROR':
        return 'Unable to connect to the server. Please check your internet connection and try again.'
      case 'CORS_ERROR':
        return 'There is a configuration issue preventing the request. Please contact your system administrator.'
      case 'AUTH_ERROR':
        return 'Password reset failed. Please try again or contact support.'
      case 'API_ERROR':
        if (error.details?.status === 429) {
          return 'Too many requests. Please wait a moment before trying again.'
        }
        if (error.details?.status >= 500) {
          return 'Server error occurred. Please try again in a few moments.'
        }
        return error.message || 'Failed to send reset instructions. Please try again.'
      default:
        return error.message || 'An unexpected error occurred while trying to reset your password.'
    }
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">
          Reset Your Password
        </h2>
        <p className="text-sm text-gray-600 mt-2">
          Enter your email address and we'll send you instructions to reset your password
        </p>
      </div>

      {errorState && (
        <div className={`mb-4 p-4 rounded-md border ${
          errorState.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-800' 
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-start">
            {errorState.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mt-0.5 mr-3 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-5 w-5 mt-0.5 mr-3 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium">{errorState.message}</p>
            </div>
          </div>
        </div>
      )}

      {!isSubmitted ? (
        <form onSubmit={handleSubmit(handleResetPassword)} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                type="email"
                id="email"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:border-fwo-blue"
                placeholder="pilot@example.com"
              />
              <Mail className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-fwo-blue text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                Sending Reset Instructions...
              </>
            ) : (
              'Send Reset Instructions'
            )}
          </button>
        </form>
      ) : (
        <div className="text-center space-y-4">
          <div className="text-green-600">
            <CheckCircle className="h-12 w-12 mx-auto mb-4" />
          </div>
          <p className="text-sm text-gray-600">
            If an account with that email address exists, you will receive password reset instructions shortly.
          </p>
          <p className="text-xs text-gray-500">
            Didn't receive an email? Check your spam folder or try again with a different email address.
          </p>
        </div>
      )}

      {onBackToLogin && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={onBackToLogin}
            className="inline-flex items-center text-sm text-fwo-blue hover:text-blue-700 underline"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Login
          </button>
        </div>
      )}

      <div className="mt-6 text-xs text-gray-500 text-center">
        <p>Only registered pilots from Airtribune can reset their password.</p>
        <p className="mt-1">Having trouble? Contact the competition organizers.</p>
      </div>
    </div>
  )
}